import React from 'react';
import { IntlShape, injectIntl } from 'react-intl';
import { connect, useDispatch } from 'react-redux';

import { getHasLoadedOptions, getJobTitles } from 'store/modules/options/selectors';
import { IReducerState, history } from 'store/main-reducer';
import { getContactDetailsData, getCompanyDetailsData } from 'store/modules/sign-up/selectors';
import { IContactDetails, ICompanyDetails, AccountantTuple } from 'types';
import { actionSetContactDetails, actionSetAccountantDetails } from 'store/modules/sign-up/actions';
import { CallbackRequestModal } from 'views/components/CallbackRequestModal/CallbackRequestModal';
import { actionSilentContactDetailsSubmit } from 'store/modules/callback-request/actions';
import { actionAccountantLookup } from 'store/modules/accountant-lookup/actions';
import { getAccountantLookupResults, getAccountantLookupIsLoading, getAccountantLookupError } from 'store/modules/accountant-lookup/selectors';
import { ContactDetailsView } from './ContactDetails';

interface ActionProps {
  setContactDetailsStep: typeof actionSetContactDetails;
  setAccountantDetails: typeof actionSetAccountantDetails;
  accountantLookup: typeof actionAccountantLookup;
}

interface ValueProps {
  hasLoadedOptions: boolean;
  defaultValues?: IContactDetails;
  jobTitles: string[];
  accountantDetails?: ICompanyDetails;
  accountantResults: AccountantTuple[];
  accountantIsLoading: boolean;
  accountantError?: string;
}

interface Props extends ActionProps, ValueProps {
  intl: IntlShape;
}

const ContactDetailsController = (props: Props) => {
  const {
    intl,
    hasLoadedOptions,
    defaultValues,
    setContactDetailsStep,
    jobTitles,
    accountantDetails,
    setAccountantDetails,
    accountantLookup,
    accountantResults,
    accountantIsLoading,
    accountantError,
  } = props;

  const dispatch = useDispatch();

  const goToNextStep = (data: IContactDetails, accountantData: ICompanyDetails) => {
    setContactDetailsStep({
      ...data,
    });

    setAccountantDetails(accountantData);

    history.push('/sign-up/company-details');

    const { contact, isAccountant } = data as IContactDetails;

    if (!contact) {
      return;
    }

    const {
      email,
      firstName,
      lastName,
      contactNumber: phone,
    } = contact;

    const {
      accountant,
      accountantBusinessName,
      accountantPhoneNumber,
      accountantName,
    } = accountantData;

    dispatch(actionSilentContactDetailsSubmit({
      email,
      phone,
      firstName,
      lastName,
      accountantName,
      accountantBusinessName: accountant?.name || accountantBusinessName,
      accountantPhoneNumber,
      isAccountant,
    }));
  };

  const goToPreviousStep = () => {
    history.push('/sign-up/pick-a-plan');
  };

  if (!hasLoadedOptions) {
    history.push('/');
  }

  return (
    <>
      <ContactDetailsView
        intl={intl}
        jobTitles={jobTitles}
        goToNextStep={goToNextStep}
        goToPreviousStep={goToPreviousStep}
        defaultValues={defaultValues}
        accountantDetails={accountantDetails}
        accountantLookup={accountantLookup}
        accountantResults={accountantResults}
        accountantIsLoading={accountantIsLoading}
        accountantError={accountantError}
      />
      <CallbackRequestModal />
    </>
  );
};


const mapStateToProps = (state: IReducerState): ValueProps => ({
  hasLoadedOptions: getHasLoadedOptions(state),
  defaultValues: getContactDetailsData(state),
  jobTitles: getJobTitles(state),
  accountantDetails: getCompanyDetailsData(state),
  accountantResults: getAccountantLookupResults(state),
  accountantIsLoading: getAccountantLookupIsLoading(state),
  accountantError: getAccountantLookupError(state),
});

const mapDispatchToProps: ActionProps = {
  setContactDetailsStep: actionSetContactDetails,
  setAccountantDetails: actionSetAccountantDetails,
  accountantLookup: actionAccountantLookup,
};

export const ContactDetails = injectIntl(connect(
  mapStateToProps,
  mapDispatchToProps,
)(ContactDetailsController));
